import { Address } from 'types'

import { standardizeCountry } from 'src/lib/country.utils'

// This function is used to parse the address from AddressInput (@vis.gl/react-google-maps library)
export function parseGoogleAddress(place) {
  let streetNumber = ''
  let route = ''
  let subpremise = ''
  let city = ''
  let state = ''
  let country = ''
  let zipCode = ''
  if (place.address_components) {
    for (const component of place.address_components) {
      if (component.types.includes('street_number')) {
        streetNumber = component.long_name
      } else if (component.types.includes('route')) {
        route = component.long_name
      } else if (component.types.includes('subpremise')) {
        subpremise = component.long_name
      } else if (
        component.types.includes('locality') ||
        component.types.includes('sublocality')
      ) {
        city = component.long_name
      } else if (component.types.includes('administrative_area_level_1')) {
        state = component.short_name
      } else if (component.types.includes('country')) {
        country = component.short_name
      } else if (component.types.includes('postal_code')) {
        zipCode = component.long_name
      }
    }
  }

  const streetAddress = `${streetNumber} ${route} ${subpremise}`
  return {
    street: streetAddress.trim(),
    city,
    state,
    country,
    zipCode,
  }
}

export const findAddressComponent = (components, type) =>
  components.find((component) => component.types.includes(type))

// This function is used to parse the address from GooglePlacePicker (@googlemaps/extended-component-library/react library)
export function parseReactGoogleAddress(place) {
  let streetNumber = ''
  let route = ''
  let subpremise = ''
  let city = ''
  let state = ''
  let country = ''
  let zipCode = ''

  const components = place.addressComponents || []

  const streetNumberComp = findAddressComponent(components, 'street_number')
  if (streetNumberComp) streetNumber = streetNumberComp.longText

  const routeComp = findAddressComponent(components, 'route')
  if (routeComp) route = routeComp.longText

  const subpremiseComp = findAddressComponent(components, 'subpremise')
  if (subpremiseComp) subpremise = subpremiseComp.longText

  // For city, check in order of preference
  const cityTypes = ['locality', 'sublocality', 'administrative_area_level_3']
  for (const type of cityTypes) {
    const cityComp = findAddressComponent(components, type)
    if (cityComp) {
      city = cityComp.longText
      break
    }
  }

  const stateComp = findAddressComponent(
    components,
    'administrative_area_level_1'
  )
  if (stateComp) state = stateComp.shortText

  const countryComp = findAddressComponent(components, 'country')
  if (countryComp) country = countryComp.shortText

  const zipCodeComp = findAddressComponent(components, 'postal_code')
  if (zipCodeComp) zipCode = zipCodeComp.longText

  const streetAddress = `${streetNumber} ${route} ${subpremise}`

  return {
    street: streetAddress.trim(),
    city,
    state,
    country,
    zipCode,
  }
}

export function formatAddress(address: Address) {
  let result = ''
  if (
    !address ||
    (!address.street && !address.city && !address.state && !address.zipCode)
  ) {
    return result
  }

  if (address.street) {
    result += address.street
  }
  if (address.city) {
    result += (result ? ', ' : '') + address.city
  }
  if (address.state) {
    result += (result ? ', ' : '') + address.state
  }
  if (address.country) {
    result += (result ? ', ' : '') + address.country
  }
  if (address.zipCode) {
    result += (result ? ', ' : '') + address.zipCode
  }
  return result
}

export type AddressFieldUpdates = {
  street?: string
  city?: string
  state?: string
  zipCode?: string
  country?: string
}

// Distinguishes a Google Places selection (at least one non-street field
// populated) from plain typing (only street changes), and builds the field
// updates accordingly — typing must not clear the other address fields.
export function buildAddressUpdates(
  value: AddressFieldUpdates
): AddressFieldUpdates {
  const nonEmpty = (v?: string) => (v ? String(v).trim() : '')

  const city = nonEmpty(value.city)
  const state = nonEmpty(value.state)
  const zipCode = nonEmpty(value.zipCode)
  const country = nonEmpty(value.country)
  const isPlaceSelection = Boolean(city || state || zipCode || country)

  const updates: AddressFieldUpdates = {}
  if (value.street !== undefined) updates.street = value.street
  if (isPlaceSelection) {
    if (city) updates.city = city
    // Uppercase to match state-code schema validation.
    if (state) updates.state = state.toUpperCase()
    if (zipCode) updates.zipCode = zipCode
    if (country) updates.country = standardizeCountry(country)
  }
  return updates
}
